import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, MessageCircle, RefreshCw } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { copyText, whatsappLink } from "../../lib/whatsapp";
import { ConfirmDialog } from "./ConfirmDialog";

export const QuickLinkTab = () => {
  const [cfg, setCfg] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get("/quick-link").then((r) => setCfg(r.data));
  }, []);
  if (!cfg) return <p className="text-muted-foreground">Loading…</p>;

  const link = `${window.location.origin}/q/${cfg.token}`;
  const msg = `*Man & Moda – Raise a Purchase Order*\n\nTap the link below to raise a fabric PO from your phone:\n${link}\n\n1. Enter the 4-digit PIN (ask the purchase head)\n2. Select vendor, add items, qty, rate and photos\n3. Enter your name in "Raised by" and tap Save PO\n\nAccounts will review and accept it in the PO Register.\nTip: open the link once and use "Add to Home screen" for one-tap access.`;
  const save = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put("/quick-link", { enabled, pin: pin || null });
      setCfg(data);
      setPin("");
      toast.success(data.enabled ? "Link is on" : "Link is off");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const regenerate = async () => {
    setConfirm(false);
    const { data } = await api.post("/quick-link/regenerate");
    setCfg(data);
    toast.success("New link created. The old link no longer works.");
  };

  return (
    <div className="space-y-3" data-testid="quick-link-tab">
      <div className="panel space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading font-bold">WhatsApp PO link</h3>
          <span className={`chip ${cfg.enabled ? "chip-Accepted" : "chip-Cancelled"}`} data-testid="quick-link-status">{cfg.enabled ? "On" : "Off"}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Purchase staff open this link from WhatsApp, enter the PIN and fill the New PO form without logging in. POs arrive in the Register as Draft, marked "Via link", for accounts to track.
        </p>
        <input readOnly className="field mono text-sm" value={link} data-testid="quick-link-url" />
        <div className="grid grid-cols-2 gap-2">
          <button className="btn btn-outline" onClick={async () => ((await copyText(link)) ? toast.success("Link copied") : toast.error("Could not copy"))} data-testid="quick-link-copy-btn">
            <Copy size={18} /> Copy link
          </button>
          <a className="btn btn-wa" href={whatsappLink(msg)} target="_blank" rel="noopener noreferrer" data-testid="quick-link-whatsapp-btn">
            <MessageCircle size={18} /> WhatsApp
          </a>
        </div>
      </div>
      <div className="panel space-y-3 p-4">
        <label className="label">{cfg.has_pin ? "Change PIN (4 digits)" : "Set PIN (4 digits) *"}</label>
        <input className="field mono tracking-[0.4em]" inputMode="numeric" maxLength={4} value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••" data-testid="quick-link-pin-input" />
        <p className="text-xs text-muted-foreground">Changing the PIN signs out everyone who opened the link with the old PIN.</p>
        <div className="grid grid-cols-2 gap-2">
          {cfg.enabled ? (
            <>
              <button className="btn btn-primary" disabled={busy || pin.length !== 4} onClick={() => save(true)} data-testid="quick-link-save-pin-btn">
                {busy && <Loader2 size={18} className="animate-spin" />} Save PIN
              </button>
              <button className="btn btn-outline btn-ghost-danger" disabled={busy} onClick={() => save(false)} data-testid="quick-link-off-btn">Turn off</button>
            </>
          ) : (
            <button className="btn btn-accent col-span-2" disabled={busy || (!cfg.has_pin && pin.length !== 4) || (pin && pin.length !== 4)} onClick={() => save(true)} data-testid="quick-link-on-btn">
              {busy && <Loader2 size={18} className="animate-spin" />} Turn link on
            </button>
          )}
        </div>
      </div>
      <button className="btn btn-outline w-full" onClick={() => setConfirm(true)} data-testid="quick-link-regenerate-btn"><RefreshCw size={18} /> Create new link</button>
      <ConfirmDialog open={confirm} onCancel={() => setConfirm(false)} onConfirm={regenerate} confirmLabel="Create new link"
        title="Replace the link?" body="The current link will stop working. Share the new link in your WhatsApp group." />
    </div>
  );
};
